import { api } from '../../core/api-client';
import type {
  MonitoringAgentToken, IssueAgentTokenInput, IssueAgentTokenResult,
  ScreenshotsPage, MonitoringSettings, MonitoringSettingsInput,
  MonitoringLiveSession, LiveStatusResponse,
  ActivityReport, AppUsageReport, ProductivityReport, ScheduleAdherenceReport,
} from './types';

export interface AnalyticsRangeOpts {
  from: string;
  to: string;
  employeeId?: string;
}

function analyticsQs(opts: AnalyticsRangeOpts, extra: Record<string, string> = {}): string {
  const qs = new URLSearchParams({ from: opts.from, to: opts.to, ...extra });
  if (opts.employeeId) qs.set('employee_id', opts.employeeId);
  return qs.toString();
}

/** Path builders for the analytics endpoints — used with `useApi()`. */
export const monitoringAnalyticsPaths = {
  activity: (opts: AnalyticsRangeOpts) =>
    `/api/monitoring/analytics/activity?${analyticsQs(opts)}`,
  apps: (opts: AnalyticsRangeOpts & { top?: number }) =>
    `/api/monitoring/analytics/apps?${analyticsQs(opts, opts.top ? { top: String(opts.top) } : {})}`,
  productivity: (opts: { from: string; to: string }) =>
    `/api/monitoring/analytics/productivity?${analyticsQs(opts)}`,
  scheduleAdherence: (opts: { from: string; to: string }) =>
    `/api/monitoring/analytics/schedule-adherence?${analyticsQs(opts)}`,
};

export interface SelfAgentSetup {
  monitored: boolean;
  connected?: boolean;
  agent_status?: string;
  last_heartbeat_at?: string | null;
  last_seen_version?: string | null;
  has_active_token?: boolean;
}

export const monitoringApi = {
  // Self-service agent setup (employee side — no monitoring.* permission)
  getAgentSetup: () =>
    api<{ setup: SelfAgentSetup }>('/api/monitoring/agent-setup/me').then((r) => r.setup),
  issueSelfToken: () =>
    api<{ token: MonitoringAgentToken; plaintext: string }>('/api/monitoring/agent-setup/me/token', { method: 'POST' }),

  getSettings: () =>
    api<{ settings: MonitoringSettings }>('/api/monitoring/settings').then((r) => r.settings),
  updateSettings: (input: MonitoringSettingsInput) =>
    api<{ settings: MonitoringSettings }>('/api/monitoring/settings', { method: 'PUT', body: input }).then((r) => r.settings),

  listAgentTokens: (employeeId?: string) =>
    api<{ tokens: MonitoringAgentToken[] }>(`/api/monitoring/agent-tokens${employeeId ? `?employee_id=${employeeId}` : ''}`).then((r) => r.tokens),
  issueAgentToken: (input: IssueAgentTokenInput) =>
    api<IssueAgentTokenResult>('/api/monitoring/agent-tokens', { method: 'POST', body: input }),
  revokeAgentToken: (id: string) =>
    api<{ ok: true }>(`/api/monitoring/agent-tokens/${id}`, { method: 'DELETE' }),

  listScreenshots: (opts: { employeeId?: string; from?: string; to?: string; appTitle?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (opts.employeeId) qs.set('employee_id', opts.employeeId);
    if (opts.from) qs.set('from', opts.from);
    if (opts.to) qs.set('to', opts.to);
    if (opts.appTitle) qs.set('app_title', opts.appTitle);
    if (opts.limit) qs.set('limit', String(opts.limit));
    return api<ScreenshotsPage>(`/api/monitoring/screenshots${qs.toString() ? '?' + qs : ''}`);
  },
  mySnapshots: (opts: { from?: string; to?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (opts.from) qs.set('from', opts.from);
    if (opts.to) qs.set('to', opts.to);
    if (opts.limit) qs.set('limit', String(opts.limit));
    return api<ScreenshotsPage>(`/api/monitoring/me/screenshots${qs.toString() ? '?' + qs : ''}`);
  },
  deleteScreenshot: (id: string) =>
    api<{ ok: true }>(`/api/monitoring/screenshots/${id}`, { method: 'DELETE' }),

  fileUrl: (id: string) => `/api/monitoring/screenshots/${id}/file`,

  startLive: (employeeId: string, opts: { duration_seconds?: number; interval_seconds?: number } = {}) =>
    api<{ session: MonitoringLiveSession }>('/api/monitoring/live-sessions', {
      method: 'POST',
      body: {
        employee_id: employeeId,
        duration_seconds: opts.duration_seconds ?? 300,
        interval_seconds: opts.interval_seconds ?? 10,
      },
    }).then((r) => r.session),
  stopLive: (sessionId: string) =>
    api<{ ok: true }>(`/api/monitoring/live-sessions/${sessionId}`, { method: 'DELETE' }),
  liveStatus: (employeeId: string) =>
    api<LiveStatusResponse>(`/api/monitoring/live-status?employee_id=${employeeId}`),

  // --- Analytics ---
  analyticsActivity: (opts: AnalyticsRangeOpts) =>
    api<ActivityReport>(monitoringAnalyticsPaths.activity(opts)),
  analyticsApps: (opts: AnalyticsRangeOpts & { top?: number }) =>
    api<AppUsageReport>(monitoringAnalyticsPaths.apps(opts)),
  analyticsProductivity: (opts: { from: string; to: string }) =>
    api<ProductivityReport>(monitoringAnalyticsPaths.productivity(opts)),
  analyticsScheduleAdherence: (opts: { from: string; to: string }) =>
    api<ScheduleAdherenceReport>(monitoringAnalyticsPaths.scheduleAdherence(opts)),
};
